(function () {
    "use strict";

    // Monkey-patch bootbox.js
    bootbox.dialog = (function (originalDialog) {
        const DEBOUNCE_TIMEOUT_MS = 400;
        const queue = [];
        let showHighestPriorityDialogTimeout = null;
        let dialogVisible = false;

        return function dialog_patched(options) {
            const isBlocking = Number.isInteger(options.blockingPriority) && options.blockingPriority > 0;

            if (Number.isInteger(options.blockingPriority) && !isBlocking) {
                console.debug("blockingPriority should be a strictly positive integer (>0).");
            }

            if (isBlocking) {
                clearTimeout(showHighestPriorityDialogTimeout);

                const originalOnHidden = typeof options.onHidden === "function" ? options.onHidden : undefined;

                options = {
                    ...options,
                    show: false,
                    onHidden: (e) => {
                        dialogVisible = false;
                        showHighestPriorityDialog();

                        try {
                            return originalOnHidden?.(e);
                        }
                        catch (error) {
                            console.error(error);
                        }
                    },
                };
            }

            const dialog = originalDialog(options);

            if (!isBlocking) {
                return dialog;
            }

            // Debounce the dialog.show to allow prioritization of the blocking dialogs.
            // eslint-disable-next-line angular/timeout-service
            showHighestPriorityDialogTimeout = setTimeout(showHighestPriorityDialog, DEBOUNCE_TIMEOUT_MS);

            enqueueDialog(dialog, options);

            return dialog;
        };

        function showHighestPriorityDialog() {
            if (dialogVisible || queue.length === 0) {
                return;
            }

            dialogVisible = true;

            const { dialog, options } = queue.shift();

            dialog.modal('show', options.relatedTarget);
        };

        function enqueueDialog(dialog, options) {
            const itemToQueue = { dialog, options };

            // Use 'LastIndex' sort variant to enqueue FIFO.
            const indexToInsertAt = _.sortedLastIndexBy(queue, itemToQueue, (i) => i.options.blockingPriority);
            queue.splice(indexToInsertAt, 0, itemToQueue);
        }
    })(bootbox.dialog);

    const LOCALSTORAGE_KEY = "NVO_wn_ts";
    const CHECK_PERIOD_MS = 1 * 60 * 60 * 1000; // hours * minutes/hour * seconds/minute * ms
    const DIALOG_PRIORITY = 10;

    angular.module("app").component("whatsNew", {
        template: `<div ng-init="vm.init()"></div>`,
        controller: Controller,
        controllerAs: "vm",
    });

    Controller.$inject = [
        "$rootScope", "$window", "controllerDecorator",
        "whatsNewOverviewPage", "whatsNewApi", "whatsNewSharedUtils",
    ];
    function Controller(
        $rootScope, $window, controllerDecorator,
        whatsNewOverviewPage, whatsNewApi, utils,
    ) {
        const vm = this;

        controllerDecorator.Decorate(vm, ["dialog"]);

        vm.init = () => {
            if (canSkipWhatsNewCheck()) {
                return;
            }

            whatsNewApi.Get(
                ({ status, data }) => {
                    if (status !== 200) {
                        return;
                    }

                    const { Items: items, Token: token, TotalItems: totalItems, PublishedSince: publishedSince } = data;

                    const mapped = items.map(i => {
                        const { html, images } = utils.extractCoverImages(i.Body);
                        return { ...i, Body: html, images };
                    });

                    let moreItemsUrl = totalItems <= items.length ? null : getMoreItemsOverviewUrl(publishedSince);

                    const $creationScope = Object.assign($rootScope.$new(true), {
                        slideDuration: 5,
                        slideIndex: 0,
                        hasNext: () => $creationScope.slideIndex < $creationScope.items.length - 1,
                        hasPrevious: () => $creationScope.slideIndex > 0,
                        disabled: false,

                        items: mapped,
                        moreItemsUrl,

                        lastSeenToken: token,

                        next: () => {
                            $creationScope.slideIndex++;
                        },
                        previous: () => {
                            $creationScope.slideIndex--;
                        },
                        closeModal: () => {
                            $creationScope.disabled = true;

                            whatsNewApi.UpdateLastSeenOn(
                                JSON.stringify($creationScope.lastSeenToken),
                                () => {
                                    localStorage.setItem(LOCALSTORAGE_KEY, new Date());
                                    vm.Dialog.CloseAll();
                                },
                                () => {
                                    $creationScope.disabled = false;
                                });
                        },
                    });

                    vm.Dialog.Custom({
                        blockingPriority: DIALOG_PRIORITY,
                        templateHtml: whatsNewTemplate,
                        scope: $creationScope,
                        size: "large",
                        onEscape: false,
                        closeButton: false,
                        title: vm.getTranslation("WhatsNew_Title"),
                        buttons: {},
                        onHidden: () => {
                            // Cleanup the scope when the modal closes.
                            $creationScope.$destroy();
                        }
                    });
                },
                error => {
                    console.error("Error while loading the what's new items.", error);
                });
        };

        function getMoreItemsOverviewUrl(publishedSince) {
            const $l = $window.location;
            const whatsNewOverviewUrl = new URL(`${$l.protocol}//${$l.host}`);
            whatsNewOverviewUrl.pathname = whatsNewOverviewPage;

            whatsNewOverviewUrl.searchParams.set("published_since", publishedSince.toISOString());

            return whatsNewOverviewUrl.href;
        }

        const whatsNewTemplate = `
                <div class="whats-new__content" ng-repeat="item in [items[slideIndex]]">
                    <div class="whats-new__image-container">
                        <figure class="whats-new__image-slide"
                            ng-repeat="imageSrc in item.images track by $index"
                            ng-style="item.images.length > 1 ? {
                                'animation-delay': ($index * slideDuration) + 's',
                                'animation-duration': (item.images.length * slideDuration) + 's',
                            } : {}"
                            ng-class="{ 'whats-new__no-anim': item.images.length === 1 }">

                            <div class="whats-new__bg-image--blur" ng-style="{ 'background-image': 'url(' + imageSrc + ')' }"></div>

                            <img ng-src="{{imageSrc}}" alt="item.Title" ng-class="{ 'whats-new__no-img': item.images === 0 }">
                            <figcaption>{{item.Title}}</figcaption>
                        </figure>
                    </div>
                    <div class="whats-new__text_container" ng-bind-html="item.Body"></div>
                </div>
                <div class="wizard-actions text-right no-shrink">
                    <button type="button"
                        class="btn btn-default pull-left"
                        i18n="Actionbar_Previous"
                        ng-click="previous()"
                        ng-if="hasPrevious()"
                        ng-disabled="disabled">
                    </button>
                    <button type="button"
                        class="btn btn-primary"
                        i18n="Actionbar_Next"
                        ng-click="next()"
                        ng-if="hasNext()"
                        ng-disabled="disabled">
                    </button>
                    <a class="btn btn-link" ng-href="{{moreItemsUrl}}" target="_blank" ng-if="!hasNext() && moreItemsUrl" i18n="Shared_ViewAll"></a>
                    <button type="button"
                        class="btn btn-secondary"
                        i18n="CloseScreen"
                        ng-click="closeModal()"
                        ng-if="!hasNext()"
                        ng-disabled="disabled">
                    </button>
            </div>`;
    }

    function canSkipWhatsNewCheck() {
        const timeStampStr = localStorage.getItem(LOCALSTORAGE_KEY);
        const timeStamp = timeStampStr && Date.parse(timeStampStr);

        return !!(timeStamp && Math.abs(new Date() - timeStamp) < CHECK_PERIOD_MS);
    }

})();